import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import Botao from '../egora/Botao';
import { gruposVisiveis, moduloDaRota } from './navegacao';
import { preferenciaRecolhido, salvarPreferenciaRecolhido } from './preferenciaMenu';
import { useEscritorio, useProcessosFavoritos, useRecentesComNome, nomeDoProcesso } from './dadosCasca';
import { useAbrirFicha } from './abrirFicha';
import { abrirPerfil } from './perfil';
import { iniciais } from './iniciais';
import './casca.css';

/** Abaixo desta largura o menu recolhe sozinho, a não ser que a pessoa tenha escolhido. */
const LARGURA_RECOLHE_SOZINHO = 1280;

const ABAS = [
  { valor: 'modulos', rotulo: 'Módulos' },
  { valor: 'favoritos', rotulo: 'Favoritos' },
  { valor: 'recentes', rotulo: 'Recentes' },
];

/**
 * Menu do ERP em trilho (grupos) e painel (módulos do grupo escolhido), como em
 * Home.dc.html. O painel tem as abas Módulos, Favoritos (processos com estrela)
 * e Recentes (fichas abertas neste aparelho).
 */
export default function MenuPrincipal() {
  const { usuarioLogado, temPermissao, logout } = useAuth();
  const { pathname } = useLocation();
  const escritorio = useEscritorio();
  const abrirFicha = useAbrirFicha();

  const grupos = useMemo(() => gruposVisiveis(temPermissao, usuarioLogado), [temPermissao, usuarioLogado]);
  const atual = moduloDaRota(pathname, grupos);

  const [preferencia, setPreferencia] = useState(() => preferenciaRecolhido());
  const [larguraMenor, setLarguraMenor] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < LARGURA_RECOLHE_SOZINHO
  );
  const [grupoEscolhido, setGrupoEscolhido] = useState(null);
  const [aba, setAba] = useState('modulos');
  const [perfilAberto, setPerfilAberto] = useState(false);

  const recolhido = preferencia ?? larguraMenor;
  const favoritos = useProcessosFavoritos(!recolhido && aba === 'favoritos');
  const recentes = useRecentesComNome(6);

  useEffect(() => {
    const aoRedimensionar = () => setLarguraMenor(window.innerWidth < LARGURA_RECOLHE_SOZINHO);
    window.addEventListener('resize', aoRedimensionar);
    return () => window.removeEventListener('resize', aoRedimensionar);
  }, []);

  // Qualquer troca de tela (pelo menu, por um link da página, pela busca ou por
  // uma ficha) recolhe o painel, para a tela nova usar a largura toda.
  const rotaAnterior = useRef(pathname);
  useEffect(() => {
    if (rotaAnterior.current === pathname) return;
    rotaAnterior.current = pathname;
    setPreferencia(true);
    salvarPreferenciaRecolhido(true);
    setGrupoEscolhido(null);
    setPerfilAberto(false);
  }, [pathname]);

  // Esc fecha o menu do perfil (o clique fora é a camada sj-camada).
  useEffect(() => {
    if (!perfilAberto) return undefined;
    const aoApertarTecla = (e) => { if (e.key === 'Escape') setPerfilAberto(false); };
    window.addEventListener('keydown', aoApertarTecla);
    return () => window.removeEventListener('keydown', aoApertarTecla);
  }, [perfilAberto]);

  // Sem grupo escolhido, o painel mostra o grupo da tela aberta.
  const grupoDoPainel = grupos.find((g) => g.id === (grupoEscolhido ?? atual?.grupo.id)) ?? grupos[0];

  const alternarRecolhido = () => {
    const novo = !recolhido;
    setPreferencia(novo);
    salvarPreferenciaRecolhido(novo);
  };

  const abrirGrupo = (id) => {
    setGrupoEscolhido(id);
    setAba('modulos');
    setPerfilAberto(false);
    if (recolhido) {
      setPreferencia(false);
      salvarPreferenciaRecolhido(false);
    }
  };

  // Escolher uma tela no menu recolhe o painel, para a tela usar a largura toda.
  const recolherAoEscolher = () => {
    setPreferencia(true);
    salvarPreferenciaRecolhido(true);
    setGrupoEscolhido(null);
  };

  const verPerfil = (secao = 'seguranca') => { setPerfilAberto(false); abrirPerfil(secao); };
  const sair = () => { setPerfilAberto(false); logout(); };

  if (!grupoDoPainel) return null;

  // Favoritos e Recentes viram a mesma lista de atalhos, como na prancheta.
  const atalhos = aba === 'favoritos'
    ? favoritos.itens.map((p) => ({ chave: `p${p.id}`, icone: 'star', cheio: true, rotulo: nomeDoProcesso(p), abrir: () => abrirFicha('processo', p) }))
    : recentes.itens.map((r) => ({ chave: `${r.tipo}${r.id}`, icone: 'history', cheio: false, rotulo: r.titulo, abrir: () => abrirFicha(r.tipo, r.registro) }));
  const carregandoAtalhos = aba === 'favoritos' ? favoritos.carregando : recentes.carregando;
  const vazioAtalhos = aba === 'favoritos'
    ? 'Marque um processo com a estrela para ele aparecer aqui.'
    : 'As fichas que você abrir aparecem aqui.';

  return (
    <nav className={`sj-nav${recolhido ? ' sj-nav--recolhida' : ''}`} aria-label="Principal">
      <div className="sj-trilho">
        <img className="sj-trilho__logo" src="/marca/egora-icone-claro.png" alt="EGORA" />
        {grupos.map((grupo) => (
          <Botao
            key={grupo.id}
            variante="fantasma"
            icone={grupo.icone}
            rotulo={grupo.rotulo}
            title={grupo.rotulo}
            className="sj-trilho__botao"
            aria-current={grupo.id === grupoDoPainel.id ? 'true' : undefined}
            onClick={() => abrirGrupo(grupo.id)}
          />
        ))}

        <div className="sj-trilho__fim">
          <Botao
            variante="fantasma"
            className="sj-trilho__avatar"
            aria-label={`Menu do perfil de ${usuarioLogado.nome}`}
            aria-haspopup="menu"
            aria-expanded={perfilAberto}
            onClick={() => setPerfilAberto((v) => !v)}
          >
            <span className="sj-avatar">{iniciais(usuarioLogado.nome)}</span>
            <span className="sj-trilho__online" aria-hidden="true" />
          </Botao>
        </div>
      </div>

      {!recolhido && (
        <div className="sj-painel-nav">
          <div className="sj-painel-nav__cabeca">
            <span className="sj-painel-nav__titulo">{grupoDoPainel.rotulo}</span>
            {escritorio.nome && <span className="sj-painel-nav__sub">{escritorio.nome}</span>}
          </div>

          <div className="sj-painel-nav__abas" role="tablist" aria-label="Navegação">
            {ABAS.map((a) => (
              <button
                key={a.valor}
                type="button"
                role="tab"
                className="sj-painel-nav__aba"
                aria-selected={aba === a.valor}
                onClick={() => setAba(a.valor)}
              >
                {a.rotulo}
              </button>
            ))}
          </div>

          {aba === 'modulos' && (
            <div className="sj-nav-lista">
              {grupoDoPainel.modulos.map((modulo) => (
                <NavLink
                  key={modulo.rota}
                  to={modulo.rota}
                  className="sj-nav-item"
                  aria-current={atual?.modulo.rota === modulo.rota ? 'page' : undefined}
                  onClick={recolherAoEscolher}
                >
                  <span className="sj-icone" aria-hidden="true">{modulo.icone}</span>
                  <span className="sj-nav-item__rotulo">{modulo.rotulo}</span>
                </NavLink>
              ))}
            </div>
          )}

          {aba !== 'modulos' && (
            <div className="sj-nav-lista">
              {atalhos.map((a) => (
                <button key={a.chave} type="button" className="sj-nav-item casca-nav-atalho" onClick={() => { a.abrir(); recolherAoEscolher(); }}>
                  <span className={`sj-icone${a.cheio ? ' sj-icone--cheio' : ''}`} aria-hidden="true">{a.icone}</span>
                  <span className="sj-nav-item__rotulo">{a.rotulo}</span>
                </button>
              ))}
              {atalhos.length === 0 && (
                <p className="casca-nav-vazio" role="status">{carregandoAtalhos ? 'Carregando…' : vazioAtalhos}</p>
              )}
            </div>
          )}
        </div>
      )}

      <Botao
        variante="fantasma"
        tamanho="sm"
        icone={recolhido ? 'chevron_right' : 'chevron_left'}
        rotulo={recolhido ? 'Abrir menu' : 'Recolher menu'}
        title={recolhido ? 'Abrir menu' : 'Recolher menu'}
        className="sj-nav__recolher"
        aria-expanded={!recolhido}
        onClick={alternarRecolhido}
      />

      {perfilAberto && (
        <>
          <div className="sj-camada" onClick={() => setPerfilAberto(false)} aria-hidden="true" />
          <div className="sj-perfil" role="menu" aria-label="Perfil">
            <div className="sj-perfil__cabeca">
              <span className="sj-avatar">{iniciais(usuarioLogado.nome)}</span>
              <span>
                <span className="sj-perfil__nome">{usuarioLogado.nome}</span>
                {usuarioLogado.email && <span className="legenda casca-perfil__email">{usuarioLogado.email}</span>}
              </span>
            </div>
            <Botao item role="menuitem" className="sj-perfil__item" onClick={() => verPerfil()}>
              <span className="sj-icone" aria-hidden="true">person</span><span>Ver perfil</span>
            </Botao>
            <Botao item role="menuitem" className="sj-perfil__item" onClick={() => verPerfil('preferencias')}>
              <span className="sj-icone" aria-hidden="true">tune</span><span>Preferências</span>
            </Botao>
            <div className="sj-perfil__sep" />
            <Botao item role="menuitem" className="sj-perfil__item" onClick={sair}>
              <span className="sj-icone" aria-hidden="true">logout</span><span>Sair</span>
            </Botao>
          </div>
        </>
      )}
    </nav>
  );
}
